import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Ticket, Sparkles, Heart, Package, Loader2, Star, Plus, Minus, HelpCircle, ArrowLeft } from 'lucide-react';
import ChestIcon from './ChestIcon';
import AnimaTicketIcon from './AnimaTicketIcon';
import Prism3D from './Prism3D';
import { UserData } from '../hooks/useUser';
import { Prism } from '../utils/prismSystem';
import { cn } from '../lib/utils';
import CurrencyIcon from './CurrencyIcon';
import { audioEngine } from '../utils/audioEngine';
import Confetti from './Confetti';
import { useTranslation } from 'react-i18next';

interface InventoryProps {
  user: UserData;
  onClose: () => void;
  onOpenBox: (tier: keyof UserData['rewardBoxes']) => void;
  onShowPrismManager: () => void;
  onRedeemTicket: () => Promise<boolean>;
  onOpenAnimaTickets: (stars: 1 | 2 | 3, count: number) => Promise<Prism[] | null>;
  onBuyStarTicket?: () => Promise<boolean>;
}

const traitColorMap: Record<string, string> = {
  red: "#f43f5e", yellow: "#fbbf24", green: "#10b981", blue: "#38bdf8", violet: "#a855f7"
};

const potentialColorMap: Record<string, string> = {
  gc_multi: "#fbbf24",
  gc_bonus: "#fbbf24",
  exp_bonus: "#10b981",
  reward_multi: "#f97316",
  hybrid_bonus: "#f97316",
  reward_flat: "#6366f1",
  score_bonus: "#a855f7",
  fever_bonus: "#f43f5e",
  time_bonus: "#38bdf8"
};

export default function Inventory({ user, onClose, onOpenBox, onShowPrismManager, onRedeemTicket, onOpenAnimaTickets, onBuyStarTicket }: InventoryProps) {
  const { t } = useTranslation();
  const [isUsing, setIsUsing] = React.useState(false);
  const [isOpening, setIsOpening] = React.useState(false);
  const [isConverting, setIsConverting] = React.useState(false);
  const [infoModal, setInfoModal] = React.useState<{ title: React.ReactNode; content: React.ReactNode } | null>(null);
  
  // Anima ticket opening states
  const [openingStars, setOpeningStars] = React.useState<1 | 2 | 3 | null>(null);
  const [displayStars, setDisplayStars] = React.useState<1 | 2 | 3 | null>(null);
  const [openCount, setOpenCount] = React.useState(1);
  const [unboxingPrisms, setUnboxingPrisms] = React.useState<Prism[] | null>(null);
  const [unboxingStep, setUnboxingStep] = React.useState<'idle' | 'shaking' | 'revealed'>('idle');

  const handleUseTicket = async () => {
    if (user.heartTickets <= 0) return;
    if (user.hearts >= 5) return;
    setIsUsing(true);
    const success = await onRedeemTicket();
    if (success) {
      // audioEngine.playSound('refill');
    }
    setIsUsing(false);
  };

  const handleConvertTicket = async () => {
    if ((user.heartTickets || 0) < 20 || !onBuyStarTicket) return;
    setIsConverting(true);
    const success = await onBuyStarTicket();
    if (success) {
      audioEngine.playClaim();
    }
    setIsConverting(false);
  };

  const handleInitiateOpen = (stars: 1 | 2 | 3) => {
    const owned = user.animaTickets?.[stars] || 0;
    if (owned <= 0) return;
    setOpeningStars(stars);
    setDisplayStars(stars);
    setOpenCount(1);
    setUnboxingStep('idle');
    setUnboxingPrisms(null);
  };

  const handlePerformOpen = async () => {
    if (!openingStars) return;
    setIsOpening(true);

    const prisms = await onOpenAnimaTickets(openingStars, openCount);
    if (!prisms) {
      setOpeningStars(null);
      setIsOpening(false);
      return;
    }

    const highestStar = Math.max(...prisms.map(p => p.stars)) as 1 | 2 | 3;

    setUnboxingStep('shaking');
    audioEngine.playOpenBox();
    
    // Wait until after the 3rd star light finishes pulsing (1.5s)
    await new Promise(r => setTimeout(r, 1500));

    setDisplayStars(highestStar);
    if (highestStar > openingStars) {
      audioEngine.playSound('success');
    }

    // Wait for the explosion animation to finish (total 2200ms -> remaining 700ms)
    await new Promise(r => setTimeout(r, 700));

    setUnboxingPrisms(prisms);
    setUnboxingStep('revealed');
    setIsOpening(false);
  };

  const boxConfigs = [
    { tier: 'bronze', color: 'text-orange-600', bg: 'bg-orange-50' },
    { tier: 'silver', color: 'text-blue-600', bg: 'bg-blue-50' },
    { tier: 'gold', color: 'text-yellow-600', bg: 'bg-yellow-50' },
    { tier: 'diamond', color: 'text-cyan-600', bg: 'bg-cyan-50' }
  ] as const;

  const maxOpenable = openingStars ? Math.min(user.animaTickets?.[openingStars] || 0, 10) : 0;

  return (
    <div className="absolute inset-0 z-[1000] flex flex-col bg-slate-50 overflow-hidden">
      {/* Dynamic Colorful Background */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        {/* Soft glowing orbs */}
        <div className="absolute top-[-10%] left-[-10%] w-[60vw] h-[60vw] bg-emerald-300/20 rounded-full blur-[80px] opacity-70 animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute top-[25%] right-[-20%] w-[70vw] h-[70vw] bg-blue-400/15 rounded-full blur-[100px] opacity-60 animate-pulse" style={{ animationDuration: '6s' }} />
        <div className="absolute bottom-[-10%] left-[10%] w-[80vw] h-[80vw] bg-purple-400/15 rounded-full blur-[100px] opacity-60 animate-pulse" style={{ animationDuration: '5s' }} />
        <div className="absolute top-[60%] right-[10%] w-[40vw] h-[40vw] bg-amber-300/15 rounded-full blur-[80px] opacity-50" />
      </div>
      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none z-0" style={{ backgroundImage: 'radial-gradient(#0f172a 2px, transparent 2px)', backgroundSize: '32px 32px' }} />
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-6 pt-12 relative z-10 px-6">
        <button onClick={onClose} className="p-3 bg-white rounded-2xl toy-3d-button border-none shadow-sm">
          <ArrowLeft className="w-6 h-6 text-slate-400" />
        </button>
        <h1 className="text-3xl font-num font-black tracking-tight text-slate-900 uppercase drop-shadow-sm">{t('inventory.title')}</h1>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-8 scrollbar-none relative z-10">
        
        {/* Resources Section */}
        <section>
          <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
            <Heart className="w-3 h-3 fill-current text-rose-400" />
            {t('inventory.sections.resources')}
          </h4>
          <div className="space-y-3">
            <div className="bg-slate-50 p-4 rounded-3xl flex items-center justify-between clay-base border-none">
              <div className="flex items-center gap-4">
                <div className="p-1 bg-rose-50 rounded-2xl">
                  <CurrencyIcon type="ticket_heart" size="sm" />
                </div>
                <div>
                  <div className="font-black text-slate-900 text-lg uppercase tracking-tight leading-none">{t('inventory.heart_ticket.name')}</div>
                  <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mt-1">{t('inventory.heart_ticket.desc')}</div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="text-3xl font-num text-rose-500 font-black pr-2">
                  {user.heartTickets || 0}
                </div>
                <button
                  onClick={handleUseTicket}
                  disabled={isUsing || (user.heartTickets || 0) <= 0 || user.hearts >= 5}
                  className="px-4 py-1.5 bg-rose-500 text-white text-[10px] font-black rounded-xl uppercase tracking-widest toy-3d-button border-none disabled:opacity-30 disabled:bg-slate-200 disabled:text-slate-400"
                >
                  {isUsing ? <Loader2 className="w-3 h-3 animate-spin" /> : user.hearts >= 5 ? t('inventory.heart_ticket.full') : t('inventory.heart_ticket.use')}
                </button>
              </div>
            </div>

            {/* Star Tickets Row */}
            <div className="bg-slate-50 p-4 rounded-3xl flex items-center justify-between clay-base border-none">
              <div className="flex items-center gap-4">
                <div className="p-1 bg-amber-50 rounded-2xl">
                  <CurrencyIcon type="ticket_star" size="sm" />
                </div>
                <div>
                  <div className="font-black text-slate-900 text-lg uppercase tracking-tight leading-none flex items-center gap-1">
                    {t('inventory.star_ticket.name')}
                    <button onClick={() => {
                      audioEngine.playSound('click');
                      setInfoModal({ title: t('inventory.star_ticket.name'), content: <p className="text-slate-500 font-bold uppercase tracking-widest text-center">{t('inventory.star_ticket.info')}</p> });
                    }} className="p-1 hover:bg-slate-200 rounded-full transition-colors text-slate-400">
                      <HelpCircle className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mt-1">{t('inventory.star_ticket.desc')}</div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="text-3xl font-num text-amber-500 font-black pr-2">
                  {user.starTickets || 0}
                </div>
                <button
                  onClick={handleConvertTicket}
                  disabled={isConverting || (user.heartTickets || 0) < 20}
                  className="px-4 py-1.5 bg-amber-500 text-white text-[10px] font-black rounded-xl uppercase tracking-widest toy-3d-button border-none disabled:opacity-30 disabled:bg-slate-200 disabled:text-slate-400"
                >
                  {isConverting ? <Loader2 className="w-3 h-3 animate-spin" /> : t('inventory.star_ticket.convert')}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Anima Tickets Section */}
        <section>
          <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
            <Ticket className="w-3 h-3 text-blue-500 fill-current" />
            {t('inventory.sections.anima_tickets')}
          </h4>
          <div className="space-y-3">
            {[1, 2, 3].map((stars) => {
              const count = user.animaTickets?.[stars as 1 | 2 | 3] || 0;
              return (
                <div key={stars} className="bg-slate-50 p-4 rounded-3xl flex items-center justify-between clay-base border-none">
                  <div className="flex items-center gap-4">
                    <div className="p-1 bg-blue-50 rounded-2xl">
                      <AnimaTicketIcon stars={stars} size="sm" />
                    </div>
                    <div>
                      <div className="font-black text-slate-900 text-lg uppercase tracking-tight leading-none flex items-center gap-1">
                        {stars} <Star className="w-4 h-4 fill-slate-900 text-slate-900 -mt-0.5" /> {t('inventory.anima_ticket.name', { stars: '' }).trim()}
                        <button onClick={() => {
                          audioEngine.playSound('click');
                          setInfoModal({ 
                            title: <span className="flex items-center justify-center gap-1">{stars} <Star className="w-5 h-5 fill-slate-900 text-slate-900 -mt-1" /> {t('inventory.anima_ticket.drop_rates_title', { stars: '' }).trim()}</span>, 
                            content: <div className="text-slate-500 font-bold uppercase tracking-widest text-center leading-loose">
                              {stars === 1 
                                ? t('inventory.anima_ticket.drop_1star').split('\n').map((line, i) => <span key={i}>{line}<br/></span>)
                                : stars === 2 
                                ? t('inventory.anima_ticket.drop_2star').split('\n').map((line, i) => <span key={i}>{line}<br/></span>)
                                : t('inventory.anima_ticket.drop_3star')}
                            </div> 
                          });
                        }} className="p-1 hover:bg-slate-200 rounded-full transition-colors text-slate-400">
                          <HelpCircle className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-3xl font-num text-blue-500 font-black pr-2">
                      {count}
                    </div>
                    <button
                      onClick={() => handleInitiateOpen(stars as 1 | 2 | 3)}
                      disabled={count <= 0}
                      className="px-4 py-1.5 bg-blue-500 text-white text-[10px] font-black rounded-xl uppercase tracking-widest toy-3d-button border-none disabled:opacity-30"
                    >
                      {t('inventory.anima_ticket.open_button')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Reward Boxes Section */}
        <section>
          <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
            <Package className="w-3 h-3 fill-current text-purple-500" />
            {t('inventory.sections.reward_boxes')}
            <button onClick={() => {
              audioEngine.playSound('click');
              setInfoModal({ 
                title: t('inventory.reward_box.rates_title'), 
                content: <div className="text-left text-[10px] font-black uppercase tracking-widest text-slate-500 space-y-4">
                  <div><span className="text-orange-600">{t('tiers.bronze')}:</span><br/>{t('inventory.reward_box.rates_bronze')}</div>
                  <div><span className="text-blue-600">{t('tiers.silver')}:</span><br/>{t('inventory.reward_box.rates_silver')}</div>
                  <div><span className="text-yellow-600">{t('tiers.gold')}:</span><br/>{t('inventory.reward_box.rates_gold')}</div>
                  <div><span className="text-cyan-600">{t('tiers.diamond')}:</span><br/>{t('inventory.reward_box.rates_diamond')}</div>
                </div> 
              });
            }} className="p-1 hover:bg-slate-200 rounded-full transition-colors -ml-1 text-slate-400">
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          </h4>
          <div className="grid grid-cols-2 gap-4">
            {boxConfigs.map(({ tier, color, bg }) => {
              const count = user.rewardBoxes[tier] || 0;
              return (
                <button 
                  key={tier}
                  disabled={count <= 0}
                  onClick={() => onOpenBox(tier as any)}
                  className={cn(
                    "p-5 rounded-[40px] flex flex-col items-center gap-3 transition-all toy-3d-button border-none relative overflow-hidden",
                    count > 0 ? "bg-white" : "bg-slate-50 opacity-40 grayscale"
                  )}
                >
                  <div className="relative">
                    <ChestIcon tier={tier as any} size="md" />
                    {count > 0 && <div className="absolute inset-0 bg-white/20 blur-xl rounded-full -z-10" />}
                  </div>
                  
                  <div className="text-center z-10">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">{t('tiers.' + tier)}</div>
                    <div className={cn("text-xl font-num font-black", color)}>x{count}</div>
                  </div>

                  {count > 0 && (
                    <div className="absolute top-3 right-3">
                       <Sparkles className="w-4 h-4 text-yellow-400 animate-pulse" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      </div>

      {/* Ticket Opening Modal / Overlay */}
      <AnimatePresence>
        {openingStars && (
          <div className="absolute inset-0 z-[1100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={cn("absolute inset-0 backdrop-blur-md", unboxingStep === 'shaking' ? "bg-slate-950/95" : "bg-slate-900/80")}
              onClick={unboxingStep === 'revealed' ? () => setOpeningStars(null) : undefined}
            />

            <AnimatePresence mode="wait">
              {unboxingStep === 'idle' && (
                <motion.div
                  key="idle"
                  initial={{ scale: 0.9, opacity: 0, y: 40 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.9, opacity: 0, y: -40 }}
                  className="game-card-3d border-none bg-white w-full max-w-md p-6 relative z-10 overflow-hidden"
                >
                  <div className="flex flex-col items-center gap-6 py-4">
                    <button onClick={() => setOpeningStars(null)} className="absolute top-5 right-5 p-2 hover:bg-slate-100 rounded-full transition-colors"><X className="w-5 h-5 text-slate-400" /></button>
                    <AnimaTicketIcon stars={openingStars} size="lg" />
                    <div className="text-center">
                      <h2 className="text-2xl font-num uppercase tracking-tight text-slate-900 flex items-center justify-center gap-1">
                        {openingStars} <Star className="w-5 h-5 fill-slate-900 text-slate-900 -mt-1" /> {t('inventory.anima_ticket.name', { stars: '' }).trim()}
                      </h2>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mt-1">{t('inventory.anima_ticket.stock', { count: user.animaTickets?.[openingStars] || 0 })}</p>
                    </div>
                    
                    {/* Quantity selector */}
                    <div className="flex items-center gap-4">
                      <button 
                        onClick={() => setOpenCount(c => Math.max(1, c - 1))} 
                        disabled={openCount <= 1}
                        className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center toy-3d-button border-none disabled:opacity-30"
                      >
                        <Minus className="w-4 h-4 text-slate-700" />
                      </button>
                      <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center clay-base border-none shadow-inner">
                        <span className="text-3xl font-num font-black text-slate-900">{openCount}</span>
                      </div>
                      <button 
                        onClick={() => setOpenCount(c => Math.min(maxOpenable, c + 1))} 
                        disabled={openCount >= maxOpenable}
                        className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center toy-3d-button border-none disabled:opacity-30"
                      >
                        <Plus className="w-4 h-4 text-slate-700" />
                      </button>
                    </div>

                    {maxOpenable > 1 && (
                      <button 
                        onClick={() => setOpenCount(maxOpenable)}
                        className="px-6 py-2 bg-slate-50 text-slate-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-colors border-none -mt-2"
                      >
                        {t('inventory.anima_ticket.set_max', { max: maxOpenable })}
                      </button>
                    )}

                    <button 
                      onClick={handlePerformOpen}
                      className="toy-3d-button border-none w-full font-num text-xl py-5 bg-blue-500 text-white"
                    >
                      {t('inventory.anima_ticket.open_count', { count: openCount })}
                    </button>
                  </div>
                </motion.div>
              )}

              {unboxingStep === 'shaking' && (
                <motion.div
                  key="shaking"
                  className="absolute inset-0 flex flex-col items-center justify-center z-20 pointer-events-none"
                >
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0, y: 100 }}
                    animate={{ scale: 2.5, opacity: 1, y: 0 }}
                    transition={{ type: "spring", damping: 15, stiffness: 200 }}
                    className="relative"
                  >
                    {/* Glowing effect behind ticket */}
                    <motion.div 
                      animate={{ 
                        opacity: [0.5, 1, 0.5],
                        scale: [1, 1.2, 1]
                      }}
                      transition={{ duration: 1, repeat: Infinity }}
                      className="absolute inset-0 bg-blue-400/50 blur-[60px] rounded-full -z-10"
                    />
                    
                    {/* The Shake (removed) */}
                    <motion.div
                      className="relative"
                    >
                      <AnimaTicketIcon stars={openingStars} coreStars={displayStars || openingStars} size="xl" />
                      {/* Bright Stars over the ticket */}
                      {(() => {
                        const currentDisplayStars = displayStars || openingStars;
                        if (currentDisplayStars === 1) {
                          return (
                            <motion.div
                              key="star-light-1"
                              animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1.1, 0.8] }}
                              transition={{ repeat: Infinity, duration: 0.5 }}
                              className="absolute inset-0 flex items-center justify-center"
                            >
                               <Star className="w-10 h-10 text-yellow-100 fill-yellow-50 blur-[1px] opacity-60" />
                            </motion.div>
                          );
                        }
                        if (currentDisplayStars === 2) {
                          return (
                            <motion.div
                              key="star-light-2"
                              animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1.2, 0.8] }}
                              transition={{ repeat: Infinity, duration: 0.5 }}
                              className="absolute inset-0 flex items-center justify-center"
                            >
                               <Star className="w-16 h-16 text-yellow-200 fill-yellow-100 blur-[2px] opacity-70" />
                            </motion.div>
                          );
                        }
                        return (
                          <motion.div
                            key="star-light-3"
                            animate={{ opacity: [0.2, 1, 0.2], scale: [0.9, 1.3, 0.9] }}
                            transition={{ repeat: Infinity, duration: 0.5 }}
                            className="absolute inset-0 flex items-center justify-center"
                          >
                             <svg width="0" height="0" className="absolute">
                               <defs>
                                 <linearGradient id="rainbow-star" x1="0%" y1="0%" x2="100%" y2="100%">
                                   <stop offset="0%" stopColor="#ef4444" />
                                   <stop offset="25%" stopColor="#eab308" />
                                   <stop offset="50%" stopColor="#22c55e" />
                                   <stop offset="75%" stopColor="#3b82f6" />
                                   <stop offset="100%" stopColor="#a855f7" />
                                 </linearGradient>
                               </defs>
                             </svg>
                             <Star 
                               className="w-32 h-32 blur-[2px] opacity-90 drop-shadow-[0_0_15px_rgba(255,255,255,0.8)]" 
                               style={{ stroke: 'url(#rainbow-star)', fill: 'transparent' }} 
                             />
                          </motion.div>
                        );
                      })()}
                    </motion.div>
                  </motion.div>

                  <motion.h3 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-2xl font-num font-black uppercase tracking-[0.3em] text-white animate-pulse mt-32 drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]"
                  >
                    {t('inventory.anima_ticket.unboxing')}
                  </motion.h3>

                </motion.div>
              )}

              {unboxingStep === 'revealed' && unboxingPrisms && (
                <motion.div
                  key="revealed"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="game-card-3d border-none bg-white w-full max-w-md p-6 relative z-10 overflow-hidden"
                >
                  <div className="flex flex-col items-center gap-5 w-full py-2">
                    <h3 className="text-2xl font-num uppercase tracking-tight text-slate-950 mb-2">{t('inventory.anima_ticket.unlocked')}</h3>
                    
                    {/* Grid of prisms */}
                    <div className="w-full grid grid-cols-4 gap-2.5 max-h-80 overflow-y-auto p-1">
                      {unboxingPrisms.map((p, i) => (
                        <motion.div 
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: i * 0.05, type: "spring", stiffness: 200, damping: 15 }}
                          key={p.id}
                          className="aspect-square bg-slate-50/70 rounded-2xl flex flex-col items-center justify-center p-2 border border-slate-100 shadow-sm relative"
                          style={{
                            boxShadow: `0 4px 10px -4px ${traitColorMap[p.color]}40`
                          }}
                        >
                          <div className="w-10 h-10 flex items-center justify-center scale-110 mb-1">
                            <Prism3D color={p.color} stars={p.stars} isCorrupted={!!p.blackStats} potentials={[p.slot1, p.slot2, p.slot3].filter(Boolean) as any} isStatic={true} />
                          </div>
                          
                          <div className="w-full flex items-center justify-center gap-0.5 mt-2 bg-slate-100/40 rounded-lg p-0.5 border border-slate-200/20">
                            <div 
                              className="w-[12px] h-[12px] rounded-md flex items-center justify-center shadow-sm shrink-0" 
                              style={{ backgroundColor: traitColorMap[p.color] }}
                            >
                              <Star className="w-2 h-2 fill-white text-white" />
                            </div>
                            <div className="w-[1px] h-3 bg-slate-300 mx-0.5 shrink-0" />
                            {[p.slot1, p.slot2, p.slot3].filter(Boolean).map((pot, idx) => (
                              <div 
                                key={idx} 
                                className="w-[12px] h-[12px] rounded-md flex items-center justify-center shadow-sm shrink-0" 
                                style={{ backgroundColor: potentialColorMap[pot!.type] }}
                              />
                            ))}
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <button 
                      onClick={() => {
                        audioEngine.playSound('click');
                        setOpeningStars(null);
                      }} 
                      className="toy-3d-button border-none w-full bg-[#fbbf24] text-slate-900 font-num text-xl py-5 mt-4"
                    >
                      {t('inventory.anima_ticket.collect_all')}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </AnimatePresence>

      {/* Info Modal */}
      <AnimatePresence>
        {infoModal && (
          <div className="absolute inset-0 z-[1200] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setInfoModal(null)} />
            <motion.div initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }} className="bg-white w-full max-w-sm rounded-3xl p-6 relative z-10 shadow-xl border-4 border-slate-100">
              <button onClick={() => setInfoModal(null)} className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full transition-colors"><X className="w-5 h-5 text-slate-400" /></button>
              <h3 className="text-xl font-num font-black uppercase text-slate-900 mb-4 text-center">{infoModal.title}</h3>
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                {infoModal.content}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
